import Vue from "vue";
import VueRouter, { RawLocation, Route } from "vue-router";
import Dashboard from "@/views/Dashboard.vue";

import AuthRoutes from "./routes/Auth";
import StyleguideRoutes from "./routes/Styleguide";
import InvestorRoutes from "./routes/Investor";
import AlertRoutes from "./routes/Alert";
import AgenciesRoutes from "./routes/Agencies";
import AdminRoutes from "./routes/Admin";
import SpecialInitiativesRoutes from "./routes/SpecialInitiatives";
import AgentsRoutes from "./routes/Agents";
import SettingsRoutes from "./routes/Settings";
import Notifications from "./routes/Notifications";
import store from "@/store";

Vue.use(VueRouter);

type NavigationError = Error & { _isRouter?: boolean };

type Navigation = (
  this: VueRouter,
  location: RawLocation,
  onComplete?: Function,
  onAbort?: (err: Error) => void
) => Promise<Route> | void;

const guardNavigation = (original: Navigation): Navigation =>
  function navigate(
    this: VueRouter,
    location: RawLocation,
    onComplete?: Function,
    onAbort?: (err: Error) => void
  ) {
    if (onComplete || onAbort) {
      return original.call(this, location, onComplete, onAbort);
    }
    return new Promise<Route>((resolve, reject) => {
      original.call(
        this,
        location,
        (route: Route) => resolve(route),
        (error: NavigationError) => {
          if (error && error._isRouter) {
            resolve(undefined as unknown as Route);
          } else {
            reject(error);
          }
        }
      );
    });
  };

VueRouter.prototype.push = guardNavigation(
  VueRouter.prototype.push as Navigation
) as unknown as typeof VueRouter.prototype.push;

VueRouter.prototype.replace = guardNavigation(
  VueRouter.prototype.replace as Navigation
) as unknown as typeof VueRouter.prototype.replace;

const routes = [
  {
    path: "/",
    name: "Dashboard",
    component: Dashboard,
  },
  ...AuthRoutes,
  ...StyleguideRoutes,
  ...InvestorRoutes,
  ...AlertRoutes,
  ...AdminRoutes,
  ...SpecialInitiativesRoutes,
  ...AgenciesRoutes,
  ...AgentsRoutes,
  ...SettingsRoutes,
  ...Notifications,
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach((to, from, next) => {
  const skipAuthentication = ["Auth", "Login", "Logout", "Forbidden"];
  const profileParam = to.query.profile;
  if (profileParam) {
    document.cookie = `adcontrolprofile=${profileParam}; path=/; max-age=20;`;
  }
  if (to.name && skipAuthentication.includes(to.name)) {
    next();
  } else {
    store.dispatch("initialize").then(
      () => {
        store.dispatch("routeGuard", to).then(
          () => {
            next();
          },
          () => {
            next("/forbidden");
          }
        );
      },
      () => {
        next("/login");
      }
    );
  }
});

export default router;
